import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Link } from "react-router-dom";

gsap.registerPlugin(ScrollTrigger);

export default function AboutPage() {
  const pageRef = useRef(null);

  useEffect(() => {
    // 🌟 Animations (SEO logic removed from here)
    const ctx = gsap.context(() => {
      gsap.from(".hero-text", { y: 40, opacity: 0, duration: 1, ease: "power3.out", stagger: 0.2 });
      gsap.utils.toArray(".animate-fade-up").forEach((section) => {
        gsap.fromTo(section, 
          { opacity: 0, y: 40 },
          { opacity: 1, y: 0, duration: 1, ease: "power2.out", scrollTrigger: { trigger: section, start: "top 85%" } }
        );
      });
    }, pageRef);
    
    return () => ctx.revert();
  }, []);

  return (
    <main className="bg-light min-h-screen pb-20" ref={pageRef}>
      
      {/* 🚀 REACT 19 NATIVE SEO HOISTING */}
      <title>About GRIS 2027 | Global Road Infratech Summit</title>
      <meta name="description" content="Discover GRIS 2027 - The Global Road Infratech Summit by IRF India Chapter. Join policymakers, developers, and leaders to transform road infrastructure." />
      <meta name="keywords" content="GRIS 2027, Road Infrastructure Summit, IRF India Chapter, Road Safety Conference, Infratech India" />
      <meta property="og:title" content="About GRIS 2027 | Global Road Infratech Summit" />
      <meta property="og:description" content="Transforming Road Infrastructure for a Safer, Smarter and Sustainable India. Join us on Jan 14-15, 2027 in New Delhi." />
      
      {/* 🧠 RICH SNIPPETS (JSON-LD Event Schema safely injected) */}
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Event",
            "name": "Global Road Infratech Summit (GRIS) 2027",
            "startDate": "2027-01-14",
            "endDate": "2027-01-15",
            "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
            "eventStatus": "https://schema.org/EventScheduled",
            "location": {
              "@type": "Place",
              "name": "Ambedkar International Centre",
              "address": { "@type": "PostalAddress", "addressLocality": "New Delhi", "addressCountry": "IN" }
            },
            "description": "Transforming Road Infrastructure for a Safer, Smarter and Sustainable India.",
            "organizer": { "@type": "Organization", "name": "International Road Federation India Chapter (IRF-IC)", "url": "https://indiairf.com" }
          })
        }}
      />

      {/* 1. CINEMATIC HERO SECTION */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden bg-primary-dark">
        <div className="absolute inset-0 z-0">
          {/* ⚡ 3. PERFORMANCE: fetchPriority and decoding added for 100 LCP Score */}
          <img 
            src="/hero-road.jpg" 
            alt="GRIS 2027 - Transforming Road Infrastructure Vision" 
            fetchPriority="high" 
            decoding="async"
            className="w-full h-full object-cover object-top opacity-30 mix-blend-luminosity" 
          />
          <div className="absolute inset-0 bg-gradient-to-b from-primary-dark/80 via-primary-dark/90 to-light"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <span className="hero-text inline-block px-4 py-1.5 bg-accent/20 border border-accent/50 text-accent rounded-full text-xs font-bold uppercase tracking-widest mb-6 backdrop-blur-sm">
            The Flagship Event
          </span>
          <h1 className="hero-text text-4xl md:text-5xl lg:text-7xl font-black text-white tracking-tight mb-6">
            About <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-[#FFB067]">GRIS 2027</span>
          </h1>
          <p className="hero-text mt-6 text-lg md:text-2xl text-white/90 max-w-3xl mx-auto font-medium leading-relaxed drop-shadow-md">
            From Vision to Impact: Transforming Road Infrastructure for a Safer, Smarter and Sustainable India.
          </p>
        </div>
      </section>

      {/* 2. OVERVIEW SECTION */}
      <section className="py-20 lg:py-32 max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          
          <div className="lg:col-span-7 lg:col-start-1 animate-fade-up">
            <span className="text-sm font-bold text-accent uppercase tracking-widest">Summit Overview</span>
            <h2 className="text-3xl md:text-4xl font-black text-primary mt-3 mb-8 leading-tight">
              A Global Exchange of Innovations & Strategies
            </h2>
            <div className="space-y-6 text-lg text-text/70 leading-relaxed font-medium">
              <p>
                The Global Road Infratech Summit (GRIS) 2027 is the flagship Conference and Exhibition of the International Road Federation India Chapter, bringing together policymakers, road agencies, infrastructure developers, consultants, technology providers, academia, and industry leaders from India and across the globe.
              </p>
              <p>
                As India continues its transformation through unprecedented investments in highways, expressways, urban mobility systems, and logistics corridors, GRIS 2027 serves as a premier platform to exchange ideas, showcase innovations, and foster partnerships that advance safe and sustainable transport infrastructure.
              </p>
              <p>
                Building upon the success of previous editions, this summit focuses on converting strategic vision into measurable impact through innovation, technology adoption, sustainability, and road safety leadership.
              </p>
            </div>
          </div>

          <div className="lg:col-span-5 animate-fade-up" style={{ animationDelay: '0.2s' }}>
            <div className="bg-white rounded-3xl p-8 lg:p-10 shadow-premium border border-slate-100 sticky top-32">
              <h3 className="text-2xl font-black text-primary mb-8 pb-4 border-b border-slate-100">At a Glance</h3>
              <ul className="space-y-6">
                <li className="flex flex-col border-b border-slate-50 pb-4">
                  <span className="text-text/50 font-bold uppercase tracking-widest text-xs mb-1">Dates</span>
                  <span className="text-primary font-black text-lg">14–15 January 2027</span>
                </li>
                <li className="flex flex-col border-b border-slate-50 pb-4">
                  <span className="text-text/50 font-bold uppercase tracking-widest text-xs mb-1">Venue</span>
                  <span className="text-primary font-black text-lg">Dr.Ambedkar International Centre<br/>New Delhi, India</span>
                </li>
                <li className="flex flex-col border-b border-slate-50 pb-4">
                  <span className="text-text/50 font-bold uppercase tracking-widest text-xs mb-1">Status</span>
                  <span className="text-primary font-black text-lg">Flagship Event of IRF India Chapter</span>
                </li>
                <li className="flex flex-col pt-2">
                  {/* ♿ 4. ACCESSIBILITY: aria-label added for Screen Readers */}
                  <Link 
                    to="/delegates" 
                    aria-label="Navigate to Delegate Registration Page"
                    className="w-full text-center py-4 bg-accent hover:bg-accent-dark text-white text-sm font-black uppercase tracking-widest rounded-xl transition-all shadow-lg hover:-translate-y-1"
                  >
                    Register as Delegate
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 3. WHO SHOULD PARTICIPATE SECTION */}
      <section className="py-24 bg-primary relative overflow-hidden animate-fade-up">
        <div className="absolute inset-0 opacity-5 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white via-transparent to-transparent"></div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <span className="text-xs font-bold text-accent uppercase tracking-widest">Audience Profile</span>
            <h2 className="text-3xl md:text-5xl font-black text-white mt-3">Who Should Participate?</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4",
                title: "Government",
                items: ["MoRTH & NHAI", "State PWDs", "Smart City Authorities", "Traffic Police"]
              },
              {
                icon: "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z",
                title: "Industry",
                items: ["Infrastructure Developers", "EPC Contractors", "Equipment Manufacturers", "ITS Providers"]
              },
              {
                icon: "M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z",
                title: "Research & Academia",
                items: ["Universities", "Research Organizations", "Technical Experts", "Transport Researchers"]
              },
              {
                icon: "M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9",
                title: "International",
                items: ["Development Banks", "Multilateral Agencies", "Global Associations", "Transport Organizations"]
              }
            ].map((audience, idx) => (
              <div key={idx} className="bg-white/10 backdrop-blur-md rounded-3xl p-8 border border-white/10 hover:-translate-y-2 transition-transform duration-300">
                <div className="w-14 h-14 bg-accent/20 rounded-2xl flex items-center justify-center mb-6 text-accent">
                  {/* ♿ 4. ACCESSIBILITY: aria-hidden and role added so screen readers ignore raw SVG code */}
                  <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true" role="presentation">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={audience.icon} />
                  </svg>
                </div>
                <h4 className="text-xl font-bold text-white mb-6">{audience.title}</h4>
                <ul className="space-y-3">
                  {audience.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-white/70 font-medium">
                      <span className="text-accent mt-0.5">•</span> {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. THE INVITATION QUOTE */}
      <section className="py-24 lg:py-32 max-w-5xl mx-auto px-6 text-center animate-fade-up">
        <svg className="w-16 h-16 mx-auto text-accent mb-8 opacity-40" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true" role="presentation">
          <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
        </svg>
        <blockquote className="text-2xl md:text-4xl font-black text-primary leading-tight mb-10">
          "The challenge before us is no longer to merely envision the future—it is to deliver measurable impact through innovation, collaboration, and decisive action."
        </blockquote>
        <div className="flex flex-col items-center justify-center">
          <div className="w-20 h-1 bg-accent rounded-full mb-6"></div>
          <h4 className="text-xl font-bold text-primary">K.K. Kapila</h4>
          <p className="text-sm font-bold text-text/50 uppercase tracking-widest mt-1">
            Patron, GRIS 2027<br/>Founder President, IRF India Chapter
          </p>
        </div>
      </section>

    </main>
  );
}